import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { yieldToMainThread } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {HTMLElement | null} */
  #variantPicker = null;

  /** @type {string | null} */
  #requestedVariantId = null;

  /** @type {string | null} */
  #previousVariantId = null;

  /** @type {string} */
  #pendingColorName = '';

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller === null || this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#handleLeatherColorSwatchClick, { signal, capture: true });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.#isLeatherColorSwipe && target && !this.closest('.quick-add-modal__content')) {
      const productId = this.dataset.mhProductId;
      const variantPicker = Array.from(
        target.querySelectorAll('variant-picker[data-template-product-match="true"]')
      ).find((picker) => picker instanceof HTMLElement && picker.dataset.productId === productId);

      if (variantPicker instanceof HTMLElement && this.slideshow) {
        this.#variantPicker = variantPicker;
        this.slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleLeatherSlideSelect, { signal });
        target.addEventListener('variant:fetch-error', this.#handleLeatherVariantFetchError, { signal });
        variantPicker.setAttribute('data-mh-gallery-controlled', 'true');
        this.dataset.mhSwipeReady = 'true';
      }

      this.#alignLeatherHeroToSelectedColor();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#variantPicker?.removeAttribute('data-mh-gallery-controlled');
    this.#variantPicker = null;
    this.#requestedVariantId = null;
    this.#previousVariantId = null;
    this.#pendingColorName = '';
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.#isLeatherColorSwipe) {
      if (String(event.detail.data.productId) !== String(this.dataset.mhProductId)) return;

      const updatedSource = event.detail.data.html;
      const productId = String(this.dataset.mhProductId || '');
      const newMediaGallery = productId
        ? updatedSource?.querySelector(`media-gallery[data-mh-product-id="${CSS.escape(productId)}"]`)
        : null;
      const mediaId = event.detail.resource?.featured_media?.id;
      const colorName = this.#pendingColorName;
      const requestedVariantId = this.#requestedVariantId;
      const focusRestoreTarget = this.#captureLeatherFocusTarget(document.activeElement);

      this.#requestedVariantId = null;
      this.#previousVariantId = null;
      this.#pendingColorName = '';

      if (!newMediaGallery) {
        if (mediaId) {
          this.slideshow?.select({ id: String(mediaId) }, undefined, { animate: false });
          this.#syncLeatherColorSwatches(mediaId);
        }

        if (colorName) this.#announceLeatherColor(this, `${colorName} rengi seçildi.`);

        return;
      }

      this.replaceWith(newMediaGallery);

      const selectedSwatch = newMediaGallery.querySelector('[data-mh-color-swatch][aria-pressed="true"]');
      const confirmedColorName =
        colorName || selectedSwatch?.getAttribute('aria-label')?.replace(/ rengini gör$/u, '') || '';
      if (confirmedColorName) {
        this.#announceLeatherColor(
          newMediaGallery,
          `${confirmedColorName} rengi ve detay fotoğrafları seçildi.`
        );
      }

      this.#restoreLeatherFocus(newMediaGallery, focusRestoreTarget, requestedVariantId);

      return;
    }

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Selects a color swatch without allowing the parent media slide to open zoom.
   * The capture listener runs before the slideshow slide's click handler.
   *
   * @param {MouseEvent} event
   */
  #handleLeatherColorSwatchClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-mh-color-swatch]') : null;
    if (!(target instanceof HTMLButtonElement) || !this.#isLeatherColorSwipe || !this.contains(target)) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = target.dataset.mhColorVariantId;
    const mediaId = target.dataset.mhColorMediaId;
    if (!variantId || !this.#variantPicker) return;

    const input = Array.from(this.#variantPicker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
      (candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === variantId
    );

    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    if (mediaId) this.#syncLeatherColorSwatches(mediaId);
    if (input.checked || this.#requestedVariantId === variantId) return;

    const previousInput = this.#variantPicker.querySelector('input[type="radio"][data-variant-id]:checked');
    if (!this.#requestedVariantId) {
      this.#previousVariantId =
        previousInput instanceof HTMLInputElement ? previousInput.dataset.variantId || null : null;
    }

    this.#requestedVariantId = variantId;
    this.#pendingColorName = target.getAttribute('aria-label')?.replace(/ rengini gör$/u, '') || input.value;
    input.click();
  };

  /**
   * Keeps the visible swatch state aligned with the selected hero media.
   *
   * @param {string | number | null | undefined} mediaId
   */
  #syncLeatherColorSwatches = (mediaId) => {
    if (!mediaId) return;

    this.querySelectorAll('[data-mh-color-swatch]').forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;
      swatch.setAttribute('aria-pressed', swatch.dataset.mhColorMediaId === String(mediaId) ? 'true' : 'false');
    });
  };

  /**
   * Captures the semantic gallery control that owns focus before server-rendered replacement.
   *
   * @param {Element | null} activeElement
   * @returns {{ kind: 'swatch'; variantId: string } | { kind: 'control'; selector: string; index: number } | null}
   */
  #captureLeatherFocusTarget = (activeElement) => {
    if (!(activeElement instanceof Element) || !this.contains(activeElement)) return null;

    const swatch = activeElement.closest('[data-mh-color-swatch]');
    if (swatch instanceof HTMLElement && this.contains(swatch)) {
      const variantId = swatch.dataset.mhColorVariantId;
      if (variantId) return { kind: 'swatch', variantId };
    }

    const arrow = activeElement.closest('.slideshow-control--previous, .slideshow-control--next');
    if (arrow instanceof HTMLElement && this.contains(arrow)) {
      const selector = arrow.classList.contains('slideshow-control--previous')
        ? '.slideshow-control--previous'
        : '.slideshow-control--next';
      const index = Array.from(this.querySelectorAll(selector)).indexOf(arrow);
      if (index >= 0) return { kind: 'control', selector, index };
    }

    const thumbnail = activeElement.closest('.slideshow-controls__thumbnail');
    if (thumbnail instanceof HTMLElement && this.contains(thumbnail)) {
      const selector = '.slideshow-controls__thumbnail';
      const index = Array.from(this.querySelectorAll(selector)).indexOf(thumbnail);
      if (index >= 0) return { kind: 'control', selector, index };
    }

    return null;
  };

  /**
   * Restores focus to the equivalent swatch, arrow, or thumbnail after gallery replacement.
   * Falls back to the confirmed color swatch if that control no longer exists or is disabled.
   *
   * @param {Element} gallery
   * @param {{ kind: 'swatch'; variantId: string } | { kind: 'control'; selector: string; index: number } | null} target
   * @param {string | null} requestedVariantId
   */
  #restoreLeatherFocus = (gallery, target, requestedVariantId) => {
    if (!target) return;

    requestAnimationFrame(() => {
      if (!gallery.isConnected) return;

      let focusTarget = null;
      if (target.kind === 'swatch') {
        focusTarget = gallery.querySelector(
          `[data-mh-color-variant-id="${CSS.escape(target.variantId)}"]`
        );
      } else {
        focusTarget = gallery.querySelectorAll(target.selector)[target.index] || null;
      }

      if (!(focusTarget instanceof HTMLElement) || (focusTarget instanceof HTMLButtonElement && focusTarget.disabled)) {
        const requestedSelector = requestedVariantId
          ? `[data-mh-color-variant-id="${CSS.escape(requestedVariantId)}"]`
          : '[data-mh-color-swatch][aria-pressed="true"]';
        focusTarget = gallery.querySelector(requestedSelector);
      }

      if (focusTarget instanceof HTMLElement && !(focusTarget instanceof HTMLButtonElement && focusTarget.disabled)) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  };

  /**
   * Announces a color update after the empty live region has been present for a paint.
   *
   * @param {Element} gallery
   * @param {string} message
   */
  #announceLeatherColor = (gallery, message) => {
    const liveRegion = gallery.querySelector('[data-mh-color-live]');
    if (!(liveRegion instanceof HTMLElement) || !message) return;

    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!gallery.isConnected || !liveRegion.isConnected) return;
        liveRegion.textContent = message;
      });
    });
  };

  /**
   * Aligns the visible hero with the server-selected color after the slideshow has initialized.
   * Horizon sets its current index before it performs the initial scroll, which can make that
   * first select a no-op for non-zero initial slides. Resetting the public current index forces
   * the component's normal select path to scroll and update aria-hidden state correctly.
   */
  #alignLeatherHeroToSelectedColor = () => {
    const selectedSwatch = this.querySelector('[data-mh-color-swatch][aria-pressed="true"]');
    const mediaId = selectedSwatch instanceof HTMLElement ? selectedSwatch.dataset.mhColorMediaId : null;
    if (!mediaId) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        if (!this.isConnected) return;

        const slideshow = /** @type {HTMLElement & { current?: number; select?: Function }} */ (this.slideshow);
        const selectedSlide = slideshow?.querySelector(
          `[data-mh-media-id="${CSS.escape(mediaId)}"][data-mh-variant-id]`
        );

        if (!(slideshow instanceof HTMLElement) || !(selectedSlide instanceof HTMLElement)) return;
        if (selectedSlide.getAttribute('aria-hidden') === 'false') return;
        if (typeof slideshow.select !== 'function') return;

        slideshow.current = -1;
        await slideshow.select({ id: mediaId }, undefined, { animate: false });
        this.#syncLeatherColorSwatches(mediaId);
      });
    });
  };

  /**
   * Selects the exact Shopify color variant represented by a user-swiped hero slide.
   * Detail and editorial slides deliberately have no variant ID and therefore do nothing.
   *
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleLeatherSlideSelect = (event) => {
    if (!event.detail.userInitiated || event.target !== this.slideshow) return;

    const slide = event.detail.slide;
    if (!(slide instanceof HTMLElement) || !this.slideshow?.contains(slide)) return;

    const variantId = slide.dataset.mhVariantId;
    if (!variantId || !this.#variantPicker) return;

    const input = Array.from(this.#variantPicker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
      (candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === variantId
    );

    if (!(input instanceof HTMLInputElement)) return;
    if (input.checked || input.disabled) return;
    if (this.#requestedVariantId === variantId) return;

    this.#syncLeatherColorSwatches(slide.dataset.mhMediaId);

    const previousInput = this.#variantPicker.querySelector('input[type="radio"][data-variant-id]:checked');

    if (!this.#requestedVariantId) {
      this.#previousVariantId =
        previousInput instanceof HTMLInputElement ? previousInput.dataset.variantId || null : null;
    }
    this.#requestedVariantId = variantId;
    this.#pendingColorName = slide.dataset.mhColorName || input.value;
    input.click();
  };

  /**
   * Restores the last confirmed variant and reveals the real picker if a gallery-driven fetch fails.
   * This keeps the displayed color, URL and purchase form aligned instead of leaving a pending color selected.
   *
   * @param {CustomEvent<{ productId?: string }>} event - The variant fetch error event.
   */
  #handleLeatherVariantFetchError = (event) => {
    if (!this.#requestedVariantId) return;
    if (String(event.detail?.productId) !== String(this.dataset.mhProductId)) return;

    const failedVariantId = this.#requestedVariantId;
    const previousVariantId = this.#previousVariantId;
    const picker = /** @type {HTMLElement & { updateSelectedOption?: (target: Element) => void }} */ (
      this.#variantPicker
    );

    if (picker && previousVariantId) {
      const previousInput = Array.from(picker.querySelectorAll('input[type="radio"][data-variant-id]')).find(
        (candidate) => candidate instanceof HTMLInputElement && candidate.dataset.variantId === previousVariantId
      );

      if (previousInput instanceof HTMLInputElement) {
        if (typeof picker.updateSelectedOption === 'function') {
          picker.updateSelectedOption(previousInput);
        } else {
          previousInput.checked = true;
        }

        const previousSlide = this.slideshow?.querySelector(`[data-mh-variant-id="${CSS.escape(previousVariantId)}"]`);
        const previousMediaId = previousSlide instanceof HTMLElement ? previousSlide.dataset.mhMediaId : null;
        if (previousMediaId) {
          this.slideshow?.select({ id: previousMediaId }, undefined, { animate: false });
          this.#syncLeatherColorSwatches(previousMediaId);
        }

        const url = new URL(window.location.href);
        url.searchParams.set('variant', previousVariantId);
        history.replaceState({}, '', url.toString());
      }
    }

    this.#requestedVariantId = null;
    this.#previousVariantId = null;
    this.#pendingColorName = '';
    picker?.removeAttribute('data-mh-gallery-controlled');
    delete this.dataset.mhSwipeReady;
    this.#announceLeatherColor(this, 'Renk seçimi güncellenemedi. Lütfen renk seçeneğini yeniden deneyin.');

    // VariantPicker schedules its URL update after yielding to the main thread. If the fetch
    // fails before that callback runs, it can briefly overwrite the immediate recovery URL.
    // Restore it once more only while the failed request is still the URL's active variant,
    // the confirmed radio is unchanged, and no newer gallery request has started.
    if (picker && previousVariantId) {
      yieldToMainThread().then(() => {
        if (!this.isConnected || this.#requestedVariantId) return;

        const confirmedInput = picker.querySelector('input[type="radio"][data-variant-id]:checked');
        if (!(confirmedInput instanceof HTMLInputElement)) return;
        if (confirmedInput.dataset.variantId !== previousVariantId) return;

        const url = new URL(window.location.href);
        if (url.searchParams.get('variant') !== failedVariantId) return;

        url.searchParams.set('variant', previousVariantId);
        history.replaceState({}, '', url.toString());
      });
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.#isLeatherColorSwipe) {
      const zoomMedia = this.refs.zoomDialogComponent?.refs.media?.[event.detail.index];
      const mediaId = zoomMedia instanceof HTMLElement ? zoomMedia.dataset.mhMediaId : null;
      if (!mediaId) return;

      const heroSlide = this.slideshow?.querySelector(
        `[data-mh-media-id="${CSS.escape(mediaId)}"][data-mh-variant-id]`
      );
      if (!(heroSlide instanceof HTMLElement)) return;

      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.#isLeatherColorSwipe ? 'carousel' : this.dataset.presentation;
  }

  get #isLeatherColorSwipe() {
    return this.dataset.mhLeatherColorSwipe === 'true';
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
